import React from 'react';
import {
  ResponsiveContainer,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Line,
  ComposedChart,
} from 'recharts';
import { useConsoleTheme } from '../../lib/theme';

// Recharts takes colours as props, so the theme is chosen here
// (docs/light-mode.md §3). Dark values are the originals, unchanged. Light:
// requests in the accent (#4D7C0F, non-text), uniques and tick labels in
// ink-3, grid and cursor as ink/accent tints, active dot ringed in raised white.
const CHART = {
  dark: {
    requests: '#B0FC38',
    requestsFillOpacity: 0.35,
    uniques: '#9aa08f',
    grid: 'rgba(255,255,255,0.04)',
    axis: 'rgba(255,255,255,0.25)',
    xTick: 'rgba(255,255,255,0.45)',
    yTick: 'rgba(255,255,255,0.35)',
    cursor: 'rgba(176,252,56,0.25)',
    dotRing: '#0a0a0f',
  },
  light: {
    requests: '#4D7C0F',
    requestsFillOpacity: 0.22,
    uniques: '#5C584C',
    grid: 'rgba(28,27,22,0.08)',
    axis: 'rgba(28,27,22,0.3)',
    xTick: '#5C584C',
    yTick: '#5C584C',
    cursor: 'rgba(77,124,15,0.45)',
    dotRing: '#FFFFFF',
  },
};

function formatShortDate(d) {
  if (!d) return '';
  const date = new Date(`${d}T00:00:00Z`);
  const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  const day = date.getUTCDate();
  return `${month} ${day}`;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="px-3 py-2.5 rounded-lg bg-[#0a0a0f] border border-white/[0.08] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)]">
      <div className="text-[11px] text-gray-500 tracking-wider uppercase mb-1.5">
        {formatShortDate(label)}
      </div>
      <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-[12px] tabular-nums">
        <span className="text-gray-500">Requests</span>
        <span className="text-[#B0FC38] font-medium text-right">{row.requests.toLocaleString()}</span>
        <span className="text-gray-500">Page views</span>
        <span className="text-white font-medium text-right">{row.pageViews.toLocaleString()}</span>
        <span className="text-gray-500">Uniques</span>
        <span className="text-[#9aa08f] font-medium text-right">{row.uniques.toLocaleString()}</span>
      </div>
    </div>
  );
}

export default function TrafficChart({ data, height = 280, loading }) {
  const { isLight } = useConsoleTheme();
  const c = isLight ? CHART.light : CHART.dark;
  if (loading) {
    return (
      <div
        className="w-full rounded-2xl bg-white/[0.02] border border-white/[0.06] animate-pulse"
        style={{ height }}
      />
    );
  }

  if (!data || data.length === 0) {
    return (
      <div
        className="w-full rounded-2xl bg-white/[0.02] border border-white/[0.06] flex items-center justify-center"
        style={{ height }}
      >
        <div className="text-sm text-gray-500">No traffic data for this range yet.</div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-white tracking-tight">Traffic over time</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">Requests and unique visitors per day.</p>
        </div>
        <div className="flex items-center gap-4 text-[11px]">
          <span className="inline-flex items-center gap-1.5 text-gray-400">
            <span className="w-2 h-2 rounded-full bg-[#B0FC38]" style={isLight ? { backgroundColor: c.requests } : undefined} />
            Requests
          </span>
          <span className="inline-flex items-center gap-1.5 text-gray-400">
            <span className="w-2 h-0.5 bg-[#9aa08f]" style={{ background: `repeating-linear-gradient(90deg, ${c.uniques} 0 4px, transparent 4px 7px)` }} />
            Uniques
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="requestsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={c.requests} stopOpacity={c.requestsFillOpacity} />
              <stop offset="100%" stopColor={c.requests} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={c.grid} vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatShortDate}
            stroke={c.axis}
            tick={{ fontSize: 11, fill: c.xTick }}
            axisLine={false}
            tickLine={false}
            minTickGap={32}
          />
          <YAxis
            stroke={c.axis}
            tick={{ fontSize: 11, fill: c.yTick }}
            axisLine={false}
            tickLine={false}
            width={50}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: c.cursor, strokeWidth: 1 }} />
          <Area
            type="monotone"
            dataKey="requests"
            stroke={c.requests}
            strokeWidth={2}
            fill="url(#requestsFill)"
          />
          <Line
            type="monotone"
            dataKey="uniques"
            stroke={c.uniques}
            strokeWidth={1.5}
            strokeDasharray="4 3"
            dot={false}
            activeDot={{ r: 4, fill: c.uniques, stroke: c.dotRing, strokeWidth: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
